import { Component, ChangeDetectionStrategy, TemplateRef, ViewChild, inject, signal, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-membership-informe-pdf',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './membership-informe-pdf.component.html',
  styleUrls: ['./membership-informe-pdf.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MembershipInformePdfComponent {
  request = input<any>(null);
  party = input<any>(null);
  vehicle = input<any>(null);

  isGeneratingPdf = signal<boolean>(false);
  pdfSrcUrl = signal<SafeResourceUrl | null>(null);
  rawPdfBlobUrl: string | null = null;

  @ViewChild('pdfPreviewModal') pdfPreviewModal!: TemplateRef<any>;

  private sanitizer = inject(DomSanitizer);
  private modalService = inject(NgbModal);

  // Obtiene el socio efectivo desde los inputs o la solicitud
  get effectiveParty(): any {
    return this.party() ?? this.request()?.party ?? null;
  }

  // Obtiene el vehiculo efectivo desde los inputs o la solicitud
  get effectiveVehicle(): any {
    return this.vehicle() ?? this.request()?.vehicle ?? null;
  }

  // Formatea el nombre completo del socio postulante
  get partyFullName(): string {
    const p = this.effectiveParty;
    const full = `${p?.names ?? ''} ${p?.lastNames ?? ''}`.trim();
    return full || (p?.names ?? 'No informado');
  }

  // Formatea el texto visible de la entidad
  get entityDisplay(): string {
    const e = this.request()?.entity ?? this.effectiveParty?.entity;
    return e ? `${e.code ?? ''} - ${e.name ?? ''}`.trim() : (this.request()?.entityCode ?? 'FPV');
  }

  // Formatea el estado legible de la solicitud
  get statusDisplay(): string {
    return this.request()?.statusObj?.name ?? this.request()?.statusSlug ?? 'PENDIENTE';
  }

  // Formatea la descripcion de marca y modelo del vehiculo
  get vehicleDescDisplay(): string {
    const v = this.effectiveVehicle;
    const brand = v?.brand?.name ?? v?.brandName ?? '';
    const model = v?.model?.name ?? v?.modelName ?? '';
    return `${brand} ${model}`.trim() || 'No informado';
  }

  // Formatea el grupo o tipo de socio
  get partyGroupDisplay(): string {
    return this.effectiveParty?.group?.name ?? this.effectiveParty?.groupSlug ?? 'Socio Activo';
  }

  // Abre el modal de previsualizacion generando el PDF
  openPreview(contentModal?: any): void {
    this.isGeneratingPdf.set(true);
    setTimeout(() => {
      const doc = this._buildPDFDocument();
      const blob = doc.output('blob');
      if (this.rawPdfBlobUrl) URL.revokeObjectURL(this.rawPdfBlobUrl);
      this.rawPdfBlobUrl = URL.createObjectURL(blob);
      this.pdfSrcUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.rawPdfBlobUrl));
      this.isGeneratingPdf.set(false);
      this.modalService.open(contentModal || this.pdfPreviewModal, { size: 'lg', backdrop: 'static' });
    }, 100);
  }

  // Descarga directa del archivo PDF
  downloadPdf(): void {
    const doc = this._buildPDFDocument();
    const plate = this.effectiveVehicle?.plate ?? 'SOLICITUD';
    doc.save(`Solicitud_Incorporacion_FPV_${plate}.pdf`);
  }

  // Construye el documento jsPDF con membrete, tabla y pie de pagina
  private _buildPDFDocument(): jsPDF {
    const doc = new jsPDF('p', 'pt', 'letter');
    const pdfWidth = doc.internal.pageSize.getWidth() - 35;
    this._renderTableToDoc(doc);
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this._addHeaderPDF(doc, this.entityDisplay, 'SOLICITUD DE INCORPORACIÓN');
      this._addFooterPDF(doc, pdfWidth, i, totalPages);
    }
    return doc;
  }

  // Renderiza la tabla HTML de antecedentes en el documento
  private _renderTableToDoc(doc: jsPDF): void {
    autoTable(doc, {
      html: '#membership-report-table', startY: 105, theme: 'grid', useCss: true,
      margin: { top: 110, bottom: 80, left: 35, right: 35 }
    });
  }

  // Agrega el encabezado institucional en cada pagina
  private _addHeaderPDF(doc: jsPDF, entityName: string, title: string): void {
    autoTable(doc, {
      startY: 28, head: [
        [{ content: 'Fondo de Protección Vehicular', styles: { fontStyle: 'normal', fontSize: 10 } }],
        [{ content: entityName, styles: { fontStyle: 'italic', fontSize: 9.5 } }]
      ],
      theme: 'plain', headStyles: { halign: 'center', cellPadding: 1, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 }
    });
    autoTable(doc, {
      startY: 72, head: [[{ content: title, styles: { fontStyle: 'bold', fontSize: 11 } }]],
      theme: 'plain', headStyles: { halign: 'center', cellPadding: 0.3, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 }
    });
  }

  // Agrega el pie de pagina numerado en cada pagina
  private _addFooterPDF(doc: jsPDF, pdfWidth: number, page: number, total: number): void {
    const today = new Date().toLocaleDateString('es-CL');
    autoTable(doc, {
      startY: 742, body: [['Impreso: ' + today, 'Sistema: FPV', 'Página ' + page + ' de ' + total]],
      theme: 'plain', bodyStyles: { fontSize: 7, cellPadding: 0.3, textColor: [0, 0, 0] },
      didDrawPage: () => { doc.setLineWidth(0.5); doc.line(35, 737, pdfWidth, 737); }
    });
  }
}
